import { stateWord } from "@lafayette/domain";
import { emptyNoteClass, hintClass, listClass } from "../../requests/components/parts";
import { Button } from "../../ui/button";
import { Fact, Facts } from "../../ui/facts";
import { Icon, type IconName } from "../../ui/icon";
import { StatusBadge, type StatusTone } from "../../ui/status-badge";
import { StatusLook } from "../../ui/status-look";
import {
  ITEM_STATUS_LABELS,
  issueWords,
  kindLabel,
  SENSITIVITY_LABELS,
  SEVERITY_LABELS,
  TIME_ZONE_SOURCE_LABELS,
} from "../labels";
import type { RequestLive } from "../queries";
import { formatDay, formatDuration } from "../time";
import {
  ageText,
  type DayView,
  type Issue,
  type Item,
  type ItemView,
  keyedIssues,
  type LinkedRequest,
  type RunItem,
  type Section,
} from "../view-model";

// The plan, day by day. Semantics for a dense plan: a heading for each day (`h3`), an ordered list of items in time order, an `h4`
// and a `<time>` for each item, a jump list to the days, and every state in words and a glyph (never colour alone). The customer's
// wording (title and customer note) and the staff-only block (staff note, provenance, creator, ids) are separate blocks with their
// own label, so a staff note can never read as something the customer sees.

export type ItemAction =
  | Readonly<{ kind: "edit" | "move" | "remove" | "fixed"; item: Item; section: Section }>
  | Readonly<{ kind: "add"; section: Section }>;

type Props = Readonly<{
  days: readonly DayView[];
  requests: ReadonlyMap<string, RequestLive>;
  /** What the last full validation run said about each item, by stable id (only for the version the page shows). */
  run: ReadonlyMap<string, RunItem> | null;
  now: Date;
  canEdit: boolean;
  onAction: (action: ItemAction) => void;
}>;

function dayAnchor(section: Section): string {
  return `day-${section.id}`;
}

export function Timeline({ days, requests, run, now, canEdit, onAction }: Props) {
  if (days.length === 0) return null;
  return (
    <div className="grid gap-4">
      {days.length > 1 ? (
        <nav aria-label="Jump to a day">
          <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
            {days.map((day) => (
              <li key={day.section.id}>
                <Button asChild variant="outline" size="sm" className="rounded-full">
                  <a href={`#${dayAnchor(day.section)}`}>
                    {day.section.localDate === null
                      ? day.heading
                      : formatDay(day.section.localDate)}
                  </a>
                </Button>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
      <ol className="m-0 grid list-none gap-8 p-0" aria-label="Days of the plan">
        {days.map((day) => (
          <li key={day.section.id}>
            <DaySection
              day={day}
              requests={requests}
              run={run}
              now={now}
              canEdit={canEdit}
              onAction={onAction}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

function DaySection({
  day,
  requests,
  run,
  now,
  canEdit,
  onAction,
}: Readonly<{
  day: DayView;
  requests: ReadonlyMap<string, RequestLive>;
  run: ReadonlyMap<string, RunItem> | null;
  now: Date;
  canEdit: boolean;
  onAction: (action: ItemAction) => void;
}>) {
  const headingId = `${dayAnchor(day.section)}-heading`;
  const count = day.items.length;
  return (
    <section
      id={dayAnchor(day.section)}
      className="grid scroll-mt-20 gap-3"
      aria-labelledby={headingId}
    >
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b-2 border-border pb-2">
        <h3 id={headingId} className="m-0 font-serif text-2xl leading-[30px] font-medium">
          {day.heading}
        </h3>
        <p className={`${hintClass} flex-1`}>
          {day.section.timeZone === null ? "No time zone" : day.section.timeZone} · {count}{" "}
          {count === 1 ? "item" : "items"}
        </p>
        {canEdit ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction({ kind: "add", section: day.section })}
          >
            Add item to{" "}
            {day.section.localDate === null ? "this group" : formatDay(day.section.localDate)}
          </Button>
        ) : null}
      </header>
      {/* The list is named "Items on <heading>" by id: the heading holds the day title staff typed, so it never goes into an attribute. */}
      <span id={`${headingId}-items`} hidden>
        Items on
      </span>
      {count === 0 ? (
        <p className={emptyNoteClass}>Nothing planned on this day yet.</p>
      ) : (
        <ol className={`${listClass} gap-2`} aria-labelledby={`${headingId}-items ${headingId}`}>
          {day.items.map((view) => (
            <ItemRow
              key={view.item.id}
              view={view}
              section={day.section}
              requests={requests}
              run={run}
              now={now}
              canEdit={canEdit}
              onAction={onAction}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

/** A state of an item: a glyph and words, never colour alone. It may wrap, because a state can be long. */
function ItemBadge({
  icon,
  text,
  tone = "neutral",
}: Readonly<{ icon: IconName; text: string; tone?: StatusTone }>) {
  return (
    <StatusBadge
      tone={tone}
      glyph={<Icon name={icon} />}
      className="h-auto max-w-full py-1 text-left whitespace-normal"
    >
      {text}
    </StatusBadge>
  );
}

/**
 * One linked request. The state is the live read of the request when there is one (it moves while the page is open); else the state the
 * plan was read with. A closed request says so. A payment quote or payment request is named, never a payment state.
 */
function RequestBadge({
  request,
  live,
}: Readonly<{ request: LinkedRequest; live: RequestLive | undefined }>) {
  const state = live?.status === "OK" ? live.state : request.state;
  const closed =
    live?.status === "OK" ? request.terminal && live.state === request.state : request.terminal;
  return (
    <ItemBadge
      icon={"mail-01"}
      text={`${stateWord("request", "staffLong", state)}${closed ? " (closed)" : ""}${request.hasPayment ? " · payment on file" : ""}`}
    />
  );
}

const EVIDENCE_WORDS = {
  VERIFIED: { icon: "checkmark-circle-02", word: "verified" },
  STALE: { icon: "hourglass", word: "stale" },
  UNVERIFIED: { icon: "help-circle", word: "not verified" },
} as const;

const BASIS_WORDS: Readonly<Record<string, string>> = {
  TRAFFIC_UNAWARE: "no traffic data",
  TRAFFIC_AWARE: "with traffic",
  STATIC_HEURISTIC: "rough estimate",
};

/** The stored reading of one kind of evidence for one item, with its freshness. No provider is named. */
function EvidenceBadge({
  label,
  state,
  observedAt,
  extra,
  now,
}: Readonly<{
  label: string;
  state: "VERIFIED" | "STALE" | "UNVERIFIED";
  observedAt: string | null;
  extra: string | null;
  now: Date;
}>) {
  const words = EVIDENCE_WORDS[state];
  const age =
    observedAt === null
      ? ""
      : ` ${ageText(Math.max(0, Math.round((now.getTime() - Date.parse(observedAt)) / 60_000)))}`;
  return (
    <ItemBadge
      icon={words.icon}
      text={`${label} ${words.word}${age}${extra === null ? "" : ` · ${extra}`}`}
      tone={state === "VERIFIED" ? "success" : "warning"}
    />
  );
}

/** The end date, shown whenever it differs from the start date (each read in its own zone), so an overnight item is never ambiguous. */
function EndDate({ view }: Readonly<{ view: ItemView }>) {
  if (!view.endsOnOtherDate || view.end === null) return null;
  return <span className="font-semibold"> (ends {formatDay(view.end.date)})</span>;
}

function timeText(view: ItemView): string {
  const { item, start, end } = view;
  if (item.timingKind === "ALL_DAY") return "All day";
  if (item.timingKind === "UNTIMED" || start === null) return "No time yet";
  return end === null ? start.time : `${start.time}–${end.time}`;
}

function ItemRow({
  view,
  section,
  requests,
  run,
  now,
  canEdit,
  onAction,
}: Readonly<{
  view: ItemView;
  section: Section;
  requests: ReadonlyMap<string, RequestLive>;
  run: ReadonlyMap<string, RunItem> | null;
  now: Date;
  canEdit: boolean;
  onAction: (action: ItemAction) => void;
}>) {
  const { item, start, end } = view;
  const kind = kindLabel(item.kind);
  const titleId = `${view.anchor}-title`;
  const nameId = `${view.anchor}-name`;
  const read = view.read;
  const catalogState = read?.catalogState ?? null;
  const ran = run?.get(item.lineageId) ?? null;
  const evidence = read?.evidence ?? null;
  const linked = read?.requests ?? [];
  const hiddenRequests = read === null ? 0 : Math.max(0, (read.requestsTotal ?? 0) - linked.length);
  const entered = item.enteredLocalStart;
  const derived = start === null ? null : `${start.date}T${start.time}`;
  const connector = item.kind === "TRANSIT" || item.kind === "BUFFER";
  const cancelled = item.itemStatus === "CANCELLED";

  return (
    <li
      id={view.anchor}
      className="min-w-0 scroll-mt-20 [&:target>article]:ring-2 [&:target>article]:ring-ring"
    >
      <article
        aria-labelledby={titleId}
        className={`grid gap-3 rounded-lg border-l-4 bg-card p-4 ${cancelled ? "border-l-muted-foreground" : "border-l-primary"}`}
      >
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-[1_1_16rem] items-start gap-3">
            <span
              aria-hidden="true"
              className="grid size-9 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground [&_svg]:size-4"
            >
              <Icon name={kind.icon} />
            </span>
            <div className="grid min-w-0 gap-0.5">
              <p className="m-0 text-sm font-bold tabular-nums">
                {item.startsAt !== null && start !== null ? (
                  <>
                    <time dateTime={item.startsAt}>{start.time}</time>
                    {view.crossZone || end === null ? null : (
                      <>
                        –<time dateTime={item.endsAt ?? undefined}>{end.time}</time>
                        <EndDate view={view} />
                      </>
                    )}
                    {view.crossZone && end !== null ? (
                      <>
                        {" "}
                        <span className={zoneClass}>{start.zone}</span> →{" "}
                        <time dateTime={item.endsAt ?? undefined}>{end.time}</time>
                        <EndDate view={view} /> <span className={zoneClass}>{end.zone}</span>
                      </>
                    ) : (
                      <span className={zoneClass}> {start.zone}</span>
                    )}
                  </>
                ) : (
                  timeText(view)
                )}
                {view.durationMinutes === null ? null : (
                  <span className="font-normal text-muted-foreground">
                    {" "}
                    · {formatDuration(view.durationMinutes)}
                  </span>
                )}
              </p>
              <h4
                id={titleId}
                className={`m-0 text-base leading-6 font-semibold break-words ${cancelled ? "line-through" : ""}`}
              >
                <span id={`${view.anchor}-name`}>{item.title}</span>
                <span className="text-sm font-normal text-muted-foreground"> · {kind.word}</span>
              </h4>
            </div>
          </div>
          {canEdit ? (
            // WALK-3: each button is named by its own words and the title of the item (read by id), so the buttons of two items differ.
            // The title is never written into an attribute.
            <div className="flex min-w-0 max-w-full flex-wrap gap-2">
              <span id={`${view.anchor}-on`} hidden>
                on
              </span>
              <Button
                id={`${view.anchor}-edit`}
                variant="outline"
                size="sm"
                aria-labelledby={`${view.anchor}-edit ${nameId}`}
                onClick={() => onAction({ kind: "edit", item, section })}
              >
                Edit
              </Button>
              <Button
                id={`${view.anchor}-move`}
                variant="outline"
                size="sm"
                aria-labelledby={`${view.anchor}-move ${nameId}`}
                onClick={() => onAction({ kind: "move", item, section })}
              >
                Move
              </Button>
              <Button
                id={`${view.anchor}-fixed`}
                variant="outline"
                size="sm"
                aria-labelledby={`${view.anchor}-fixed ${view.anchor}-on ${nameId}`}
                onClick={() => onAction({ kind: "fixed", item, section })}
              >
                {item.isFixed ? "Remove fixed mark" : "Mark fixed"}
              </Button>
              <Button
                id={`${view.anchor}-remove`}
                variant="outline"
                size="sm"
                aria-labelledby={`${view.anchor}-remove ${nameId}`}
                onClick={() => onAction({ kind: "remove", item, section })}
              >
                Remove
              </Button>
            </div>
          ) : null}
        </div>

        <ul
          className="m-0 flex list-none flex-wrap gap-1.5 p-0 empty:hidden"
          aria-label="Item state"
        >
          {item.itemStatus === "PLANNED" ? null : (
            <li>
              <ItemBadge
                icon={cancelled ? "cancel-circle" : "circle"}
                text={ITEM_STATUS_LABELS[item.itemStatus] ?? item.itemStatus}
                tone={cancelled ? "warning" : "neutral"}
              />
            </li>
          )}
          {item.isFixed ? (
            <li>
              <ItemBadge icon={"flag-01"} text="Fixed: the customer holds it" />
            </li>
          ) : null}
          {item.weatherSensitivity === "NONE" ? null : (
            <li>
              <ItemBadge
                icon={"cloud-angled-rain"}
                text={SENSITIVITY_LABELS[item.weatherSensitivity] ?? "Weather sensitive"}
              />
            </li>
          )}
          {read === null ? (
            <li>
              <ItemBadge
                icon={"alert-02"}
                text="Details not available from this server version"
                tone="warning"
              />
            </li>
          ) : null}
          {linked.map((request) => (
            <li key={request.requestId}>
              <RequestBadge request={request} live={requests.get(request.requestId)} />
            </li>
          ))}
          {hiddenRequests > 0 ? (
            <li>
              <ItemBadge icon={"mail-01"} text={`and ${hiddenRequests} more linked requests`} />
            </li>
          ) : null}
          {view.acknowledged ? (
            <li>
              <ItemBadge
                icon={"checkmark-circle-02"}
                text="Staff acknowledged this travel gap"
                tone="warning"
              />
            </li>
          ) : null}
          {catalogState === "UNPUBLISHED" || catalogState === "MISSING" ? (
            <li>
              <ItemBadge icon={"alert-02"} text="Item no longer available" tone="warning" />
            </li>
          ) : null}
          {view.routeEligible && evidence !== null ? (
            <li>
              <EvidenceBadge
                label="Travel to here"
                state={evidence.route.state}
                observedAt={evidence.route.observedAt}
                extra={
                  evidence.route.basis === null ? null : (BASIS_WORDS[evidence.route.basis] ?? null)
                }
                now={now}
              />
            </li>
          ) : null}
          {view.weatherEligible && evidence !== null ? (
            <li>
              <EvidenceBadge
                label="Forecast"
                state={evidence.weather.state}
                observedAt={evidence.weather.observedAt}
                extra={
                  evidence.weather.reason === "LOCATION_CHANGED"
                    ? "the place changed, so the old forecast is not used"
                    : evidence.weather.level === null
                      ? null
                      : (WEATHER_WORDS[evidence.weather.level] ?? null)
                }
                now={now}
              />
            </li>
          ) : null}
          {view.repeated !== null && start !== null ? (
            <li>
              <ItemBadge
                icon={"repeat"}
                text={`Clock time repeats: this is the ${view.repeated} ${start.time} (${start.offsetLabel})`}
              />
            </li>
          ) : null}
          {view.endRepeated !== null && end !== null ? (
            <li>
              <ItemBadge
                icon={"repeat"}
                text={`Clock time repeats: it ends at the ${view.endRepeated} ${end.time} (${end.offsetLabel})`}
              />
            </li>
          ) : null}
        </ul>

        {view.gapBeforeMinutes === null ? null : (
          <p className={`${hintClass} text-[13px]`}>
            {view.gapBeforeMinutes >= 0
              ? `Gap after the previous item: ${formatDuration(view.gapBeforeMinutes)}`
              : `Overlaps the previous item by ${formatDuration(-view.gapBeforeMinutes)}`}
          </p>
        )}
        {connector ? (
          <p className={`${hintClass} text-[13px]`}>
            {item.kind === "TRANSIT"
              ? "Travel. Its length counts as the time between the items on each side."
              : "Buffer. Time set aside between items; it stands in for a route check on that gap."}
          </p>
        ) : null}

        {view.issues.length === 0 ? null : (
          <ul className="m-0 grid list-none gap-1 p-0" aria-label="Problems with this item">
            {keyedIssues(view.issues).map(({ key, issue }) => (
              <IssueLine key={key} issue={issue} />
            ))}
          </ul>
        )}

        {ran === null ? null : <RunLines ran={ran} />}

        <details className="group grid gap-3">
          <summary className="flex min-h-11 cursor-pointer list-none items-center gap-1 font-semibold compact:min-h-8 [&::-webkit-details-marker]:hidden">
            <Icon
              name="arrow-right-01-round"
              className="size-4 transition-transform group-open:rotate-90"
            />
            Details
          </summary>
          <fieldset className={`${blockClass} border-border bg-card`}>
            <legend className={blockLabelClass}>What the customer sees</legend>
            <p className="m-0 break-words">{item.title}</p>
            {item.customerNote === null || item.customerNote === "" ? (
              <p className={hintClass}>No customer note.</p>
            ) : (
              <p className="m-0 break-words">{item.customerNote}</p>
            )}
          </fieldset>
          <Facts>
            <Fact term="Place">{placeText(item)}</Fact>
            {catalogState === null ? null : (
              <Fact term="Catalog item">
                {catalogState === "PUBLISHED"
                  ? "Published"
                  : catalogState === "UNPUBLISHED"
                    ? "No longer published"
                    : "Not found in the catalog"}
              </Fact>
            )}
            {linked.length === 0 ? null : (
              <Fact term="Linked requests">
                {linked
                  .map(
                    (request) =>
                      `${stateWord("request", "staffLong", requests.get(request.requestId)?.status === "OK" ? (requests.get(request.requestId) as { state: string }).state : request.state)} (${request.via === "RAISED_ABOUT" ? "raised about this item" : "carried by this item"})`,
                  )
                  .join("; ")}
                {hiddenRequests > 0 ? `; and ${hiddenRequests} more` : ""}
              </Fact>
            )}
            {item.timeZoneSource === null ? null : (
              <Fact term="Zone">
                {item.timeZone} (
                {TIME_ZONE_SOURCE_LABELS[item.timeZoneSource] ?? item.timeZoneSource})
              </Fact>
            )}
            {entered !== null && derived !== null && entered !== derived ? (
              <Fact term="Entered as">
                {entered.replace("T", " ")} (the zone rules now read it differently)
              </Fact>
            ) : null}
          </Facts>
          <fieldset
            className={`${blockClass} border-2 border-dashed border-info-border bg-info-soft text-info`}
          >
            <legend className={blockLabelClass}>Staff only</legend>
            {item.staffNote === null || item.staffNote === "" ? (
              <p className="m-0">No staff note.</p>
            ) : (
              <p className="m-0 break-words">{item.staffNote}</p>
            )}
            <Facts>
              <Fact term="Stable id">
                <span className="font-mono text-xs break-all">{item.lineageId}</span>
              </Fact>
              <Fact term="Added by">
                {item.createdByActorKind} · {item.provenance}
              </Fact>
            </Facts>
          </fieldset>
        </details>
      </article>
    </li>
  );
}

const zoneClass = "text-[13px] font-normal text-muted-foreground";
const blockClass = "m-0 grid min-w-0 gap-1 rounded-sm border px-3 py-2 [&_p]:m-0";
const blockLabelClass = "px-1 text-xs font-bold tracking-wider uppercase";
/** The box of one problem (shared with the Checks panel). */
export const issueClass =
  "rounded-sm border border-border bg-surface-sunken px-2.5 py-1.5 leading-6";

const EDGE_WORDS: Readonly<Record<string, string>> = {
  FEASIBLE: "feasible",
  TIGHT: "tight",
  INFEASIBLE: "too short",
  UNVERIFIED: "not verified",
};
const MODE_WORDS: Readonly<Record<string, string>> = {
  WALK: "on foot",
  DRIVE: "by car",
  TRANSIT: "by transit",
};
const WEATHER_WORDS: Readonly<Record<string, string>> = {
  NONE: "no risk",
  WATCH: "watch",
  HIGH: "high risk",
  SEVERE: "severe risk",
};

/** What the last full validation run said about one item: its travel leg, its forecast and its location. Closed words and numbers. */
function RunLines({ ran }: Readonly<{ ran: RunItem }>) {
  const locations = ran.issues.filter(
    (issue) => issue.code.startsWith("LOCATION_") || issue.code === "ITEM_ZONE_LOCATION_MISMATCH",
  );
  if (ran.edgeIn === null && ran.weather === null && locations.length === 0 && !ran.review)
    return null;
  return (
    <ul
      className="m-0 grid list-none gap-1 rounded-sm bg-surface-sunken p-3 text-sm"
      aria-label="Full validation result for this item"
    >
      {ran.edgeIn === null ? null : (
        <li>
          <strong>Travel to here ({MODE_WORDS[ran.edgeIn.mode] ?? ran.edgeIn.mode}):</strong>{" "}
          {EDGE_WORDS[ran.edgeIn.status] ?? ran.edgeIn.status}
          {ran.edgeIn.gapMinutes === null || ran.edgeIn.requiredMinutes === null
            ? ""
            : `. Gap ${formatDuration(ran.edgeIn.gapMinutes)}, needs ${formatDuration(ran.edgeIn.requiredMinutes)}`}
          .
        </li>
      )}
      {ran.weather === null || ran.weather.state === "NOT_SENSITIVE" ? null : (
        <li>
          <strong>Forecast:</strong>{" "}
          {ran.weather.state === "VERIFIED"
            ? (WEATHER_WORDS[ran.weather.level ?? "NONE"] ?? "read")
            : ran.weather.state === "OUT_OF_RANGE"
              ? "too far ahead to forecast"
              : "not verified"}
          .
        </li>
      )}
      {locations.map((issue) => (
        <li key={issue.code}>
          <strong>Place:</strong> {issueWords(issue.code).title}.
        </li>
      ))}
      {ran.review ? (
        <li>
          <strong>Concierge review:</strong> a severe forecast. Review this item with the customer.
        </li>
      ) : null}
    </ul>
  );
}

function placeText(item: Item): string {
  const parts = [item.locationName, item.locality, item.countryCode].filter(
    (part): part is string => part !== null && part !== "",
  );
  return parts.length === 0 ? "No place" : parts.join(", ");
}

function IssueLine({ issue }: Readonly<{ issue: Issue }>) {
  const words = issueWords(issue.code);
  const severity = SEVERITY_LABELS[issue.severity];
  return (
    <li className={issueClass}>
      <StatusLook look={severity}>{severity.word}</StatusLook> <strong>{words.title}.</strong>{" "}
      <span>{words.detail}</span>
    </li>
  );
}
